import ReactDOM from 'react-dom';
import { Provider as ReduxProvider } from 'react-redux';
import moment from 'moment';
import momentDurationFormatSetup from 'moment-duration-format';
import ThemedStyleSheet from 'react-with-styles/lib/ThemedStyleSheet';
import aphroditeInterface from 'react-with-styles-interface-aphrodite';
import ThemeProvider from 'common/material-ui-theme';
import configureMoment from 'common/utils/moment-configuration';
import App from './App';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { datePickerStyles } from './styles/datepicker-styles';
import store from './state/store/configureStore';
import BrowserStorageAccessor from './common/utils/BrowserStorageAccessor';
import {
  LOCAL_STORAGE_KEYS,
  SESSION_STORAGE_KEYS,
  DAYS_PARAM,
  DD_LOGS_CONSTS,
  SURVEY_PATH_PATTERN
} from './common/constants/constants';
import { omitFromQsAndSaveInSessionStorage } from 'common/utils/utils';
import * as initService from './common/services/init.service';
import { initRum } from 'common/services/rum.service';
import { Provider as UseHttpProvider } from 'use-http';
import { options } from 'common/config/use-http.config';
import Highcharts from 'highcharts';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { LinuiTheme, COLOR_MODE } from '@linearb/linui-common';
import packageJson from '../package.json';
import './main.css';
import {
  storeToken,
  clearToken,
  domain
} from './common/services/token.service';

import { logAppVersion } from 'common/utils/logAppVersion';

Highcharts.AST.allowedAttributes.push('data-cy');

// eslint-disable-next-line no-console
logAppVersion('linweb', process.env.REACT_APP_TAG);
logAppVersion(
  'linui-common',
  packageJson.dependencies['@linearb/linui-common']
);
logAppVersion(
  'linjs-common',
  packageJson.dependencies['@linearb/linjs-common']
);
window.REACT_APP_TAG = process.env.REACT_APP_TAG;

const env =
  window.location.hostname === 'localhost'
    ? 'localhost'
    : window.location.hostname.includes('-dev')
    ? 'dev'
    : 'prod';
window.DD_LOGS && window.DD_LOGS.logger.addContext('env', env);

if (process.env.REACT_APP_RUN_IN_MOCK_MODE) {
  const { worker } = require('./msw-mock/msw.browser');
  worker.start({ onUnhandledRequest: 'bypass' });
}

ThemedStyleSheet.registerInterface(aphroditeInterface);
ThemedStyleSheet.registerTheme(datePickerStyles);

// Setup moment duration format
momentDurationFormatSetup(moment);

// Configure moment
configureMoment(moment);

// Store onboarding token
const urlParams = new URLSearchParams(window.location.search);

const refAccountId = urlParams.get('refAccountId');
const tokenParam = urlParams.get('ht');

if (tokenParam) {
  const expirationTime = new Date();
  expirationTime.setMinutes(expirationTime.getMinutes() + 15);

  clearToken();
  storeToken(tokenParam, {
    expires: expirationTime,
    domain
  });

  // Remove the token parameter from URL and refresh
  urlParams.delete('ht');
  const newUrl =
    window.location.pathname +
    (urlParams.toString() ? `?${urlParams.toString()}` : '');
  window.history.replaceState({}, '', newUrl);
  setTimeout(
    () =>
      window.DD_LOGS &&
      window.DD_LOGS.logger.info(DD_LOGS_CONSTS.used_url_token),
    10 * 1000 // delay to ensure user prop are set
  );
}

if (refAccountId) {
  omitFromQsAndSaveInSessionStorage(
    SESSION_STORAGE_KEYS.REF_ACCOUNT_ID,
    'refAccountId'
  );
}

const hubspotId = urlParams.get('hid');
if (hubspotId) {
  BrowserStorageAccessor().session.set(
    SESSION_STORAGE_KEYS.HUBSPOT_ID,
    hubspotId
  );
}

// Extract last segment of the path and store as survey ID only if isSurveyFlow
if (window.location.pathname.includes(SURVEY_PATH_PATTERN)) {
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  const surveyId = pathSegments[pathSegments.length - 1];
  if (surveyId) {
    BrowserStorageAccessor().session.set(
      SESSION_STORAGE_KEYS.SURVEY_ID,
      surveyId
    );
  }
}

// Hotfix in order to clear key for existing customers
// TODO: Remove that hotfix code after 15.12.2020
const performedOnboardingAt = localStorage.getItem(
  LOCAL_STORAGE_KEYS.SHOW_IS_THAT_YOUR_TEAM_BAR
);
if (
  performedOnboardingAt &&
  moment().diff(moment(performedOnboardingAt), DAYS_PARAM) > 6
) {
  // If the user have 'performedOnboardingAt' localstorage key without org id prefix
  // and the valye of the key is older then 3 days -> delete the key
  localStorage.removeItem(LOCAL_STORAGE_KEYS.SHOW_IS_THAT_YOUR_TEAM_BAR);
}

initService.initialize();
initRum();

ReactDOM.render(
  <LinuiTheme initialMode={COLOR_MODE.LIGHT_MODE} applyGlobalStyles={false}>
    <ReduxProvider store={store}>
      <ThemeProvider>
        <UseHttpProvider options={options}>
          <App />
        </UseHttpProvider>
      </ThemeProvider>
    </ReduxProvider>
  </LinuiTheme>,
  document.getElementById('root')
);
